// src/pages/shipment-details/ShipmentDetails.jsx
// @ts-nocheck
import React, { useEffect, useState, useCallback, useMemo, useRef } from "react";
import axios from "axios";
import { parseISO, format } from "date-fns";
import { useLocation, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { getOrderStatusName, OrderBusinessStatusEnum } from "../utils/enums";
import { formatInIST } from "../utils/dateHelpers";
import ClaimUploadModal from "../components/ClaimUploadModal";
import CreateTicketModal from "../components/support/CreateTicketModal";
import ticketService from "../services/ticketService";
import useAuth from "../hooks/useAuth";

/* ----------------------------- Helpers ----------------------------- */
// Status codes that need support ticket button
const STATUSES_REQUIRING_SUPPORT = [
  OrderBusinessStatusEnum.PICKUP_FAILED.code,
  OrderBusinessStatusEnum.FAILED_DELIVERY.code,
  OrderBusinessStatusEnum.RTO.code,
  OrderBusinessStatusEnum.RTO_FAILED.code,
  OrderBusinessStatusEnum.DAMAGED.code,
  OrderBusinessStatusEnum.SHIPMENT_DELAYED.code,
  OrderBusinessStatusEnum.LOST.code,
  OrderBusinessStatusEnum.CONTACT_CUSTOMER_CARE.code,
  OrderBusinessStatusEnum.SHIPMENT_HELD.code,
  OrderBusinessStatusEnum.RTO_INTRANSIT.code,
  OrderBusinessStatusEnum.RTO_SHIPMENT_DELAY.code,
  OrderBusinessStatusEnum.ORDER_CANCELLATION_PENDING.code,
  OrderBusinessStatusEnum.PICKUP_CANCELLED_BY_CARRIER.code,
  OrderBusinessStatusEnum.UNKNOWN.code,
];

function statusPillTone(code) {
  const c = String(code ?? "");
  if (c === "100") return "bg-emerald-100 text-emerald-700 ring-1 ring-emerald-200";
  if (["110", "120", "170"].includes(c)) return "bg-rose-100 text-rose-700 ring-1 ring-rose-200";
  return "bg-sky-100 text-sky-700 ring-1 ring-sky-200";
}
const safeMs = (iso) => {
  if (!iso) return -Infinity;
  try {
    const d = parseISO(iso);
    return Number.isNaN(d.getTime()) ? -Infinity : d.getTime();
  } catch {
    return -Infinity;
  }
};
const prettyMoney = (n) => `₹${Number(n ?? 0).toFixed(2)}`;
const gramsToKg = (g) => (g || g === 0 ? `${(Number(g) / 1000).toFixed(2)} kg` : "N/A");

/** Latest created_date_time from History API business codes (for “Last updated” + Order Date tile) */
const getLatestHistoryUpdate = (statuses = []) => {
  if (!Array.isArray(statuses) || statuses.length === 0) return null;
  const latest = statuses.reduce((best, cur) => {
    const bestMs = safeMs(best?.created_date_time);
    const curMs = safeMs(cur?.created_date_time);
    return curMs >= bestMs ? cur : best;
  });
  return latest?.created_date_time || null;
};

/* ================================ Page ================================ */
export default function ShipmentDetails() {
  const { state } = useLocation();
  const navigate = useNavigate();
  const { orderRef } = useParams();
  const [sp] = useSearchParams();
  const fromPage = Number(sp.get("page") || state?.fromPage || 1);

  const [shipment, setShipment] = useState(state?.shipment || null);
  const [businessStatuses, setBusinessStatuses] = useState([]);
  const [isFetching, setIsFetching] = useState(false);
  const [labelError, setLabelError] = useState("");

  // Claim modal controls
  const [claimModalOpen, setClaimModalOpen] = useState(false);
  const [claimModalType, setClaimModalType] = useState("damage"); // "damage" | "weight_dispute"

  const openDamageClaim = () => {
    setClaimModalType("damage");
    setClaimModalOpen(true);
  };

  const openWeightDispute = () => {
    setClaimModalType("weight_dispute");
    setClaimModalOpen(true);
  };

  // Support ticket modal controls
  const [supportModalOpen, setSupportModalOpen] = useState(false);
  const [supportOrderContext, setSupportOrderContext] = useState(null);
  const { getMerchantId } = useAuth();

  const handleOpenSupport = async () => {
    const merchantId = getMerchantId();
    if (!merchantId) {
      alert("Please log in to create a support ticket");
      return;
    }

    const sh = shipment;
    const orderRef = sh?.order_reference_number;
    if (!orderRef) {
      alert("Order reference number not found");
      return;
    }

    try {
      // Try to get existing ticket
      const existingTicket = await ticketService.getTicketByOrder(merchantId, orderRef);
      // If ticket exists, navigate to it
      navigate(`/support/tickets/${existingTicket.public_id}`);
    } catch (err) {
      // If 404, ticket doesn't exist - open create modal
      if (err?.response?.status === 404) {
        setSupportOrderContext({
          orderReferenceNumber: orderRef,
          awbNumber: sh?.awb_number,
          customerName: sh?.drop_name,
          customerPhone: sh?.drop_phone,
          customerEmail: sh?.drop_email,
        });
        setSupportModalOpen(true);
      } else {
        alert(err?.response?.data?.message || "Failed to check for existing ticket");
      }
    }
  };

  const printRootRef = useRef(null);

  /* Latest code + friendly name */
  const latestBusinessCode = useMemo(() => {
    if (!businessStatuses.length) return null;
    const latest = businessStatuses.reduce((best, cur) => {
      const bestMs = Math.max(safeMs(best.order_status_time), safeMs(best.created_date_time));
      const curMs = Math.max(safeMs(cur.order_status_time), safeMs(cur.created_date_time));
      return curMs >= bestMs ? cur : best;
    });
    return latest?.order_status_code ?? null;
  }, [businessStatuses]);

  const currentStatusName = useMemo(() => {
    if (latestBusinessCode) return getOrderStatusName(String(latestBusinessCode));
    if (shipment?.latest_order_status_code) return getOrderStatusName(String(shipment.latest_order_status_code));
    return shipment?.latest_order_status?.replace(/_/g, " ") || "N/A";
  }, [latestBusinessCode, shipment]);

  /* Fetch by orderRef if needed */
  useEffect(() => {
    (async () => {
      if (!shipment && orderRef) {
        try {
          setIsFetching(true);
          const merchantId = localStorage.getItem("merchantId");
          const authToken = localStorage.getItem("authToken");
          if (!merchantId || !authToken) return;
          const res = await axios.post(
            `${process.env.REACT_APP_OMS_BASE_URL}${process.env.REACT_APP_SEARCH_ORDERS}`,
            { merchant_id: merchantId, order_reference_number: orderRef, limit: 1, skip: 0 },
            { headers: { Authorization: authToken, "x-merchant-id": merchantId, "Content-Type": "application/json" } }
          );
          setShipment(res.data?.orders?.[0] || null);
        } catch (e) {
          console.error("Failed to fetch shipment:", e);
        } finally {
          setIsFetching(false);
        }
      }
    })();
  }, [orderRef, shipment]);

  /* Fetch history */
  const fetchOrderHistory = useCallback(async (orderReferenceNumber, signal) => {
    try {
      const merchantId = localStorage.getItem("merchantId");
      const authToken = localStorage.getItem("authToken");
      if (!merchantId || !authToken || signal?.aborted) return;

      const apiUrl = `${process.env.REACT_APP_OMS_BASE_URL}/v2/oms/order/tracking/history`;
      const response = await axios.post(
        apiUrl,
        { order_reference_number: orderReferenceNumber },
        { headers: { "x-merchant-id": merchantId, Authorization: authToken, "Content-Type": "application/json" }, signal }
      );

      const statuses = (response.data.order_processing_business_status_codes || [])
        .slice()
        .sort((a, b) => safeMs(a.order_status_time || a.created_date_time) - safeMs(b.order_status_time || b.created_date_time));
      setBusinessStatuses(statuses);
    } catch (error) {
      console.error("Error fetching order history:", error);
    }
  }, []);

  useEffect(() => {
    const ref = shipment?.order_reference_number || orderRef;
    if (!ref) return;
    const controller = new AbortController();
    fetchOrderHistory(ref, controller.signal);
    return () => controller.abort();
  }, [shipment?.order_reference_number, orderRef, fetchOrderHistory]);

  /* Actions */
  const handleCancel = async () => {
    if (!shipment?.awb_number) return;
    const confirmCancel = window.confirm(`Are you sure you want to cancel AWB ${shipment.awb_number}?`);
    if (!confirmCancel) return;
    try {
      const authToken = localStorage.getItem("authToken");
      if (!authToken) return;
      await axios.post(
        `${process.env.REACT_APP_OMS_BASE_URL}${process.env.REACT_APP_CANCEL_ORDER_URL}`,
        { waybill: shipment.awb_number },
        { headers: { Authorization: authToken, "Content-Type": "application/json" } }
      );
      navigate("/all-shipments", { state: { page: fromPage } });
    } catch (err) {
      console.error("Cancel failed:", err);
    }
  };

  const refetchLabel = async () => {
    try {
      setLabelError("");
      const merchantId = localStorage.getItem("merchantId");
      const authToken = localStorage.getItem("authToken");
      if (!merchantId || !authToken || !shipment?.order_reference_number) return;
      const apiUrl = `${process.env.REACT_APP_OMS_BASE_URL}${process.env.REACT_APP_GENERATE_REFRESH_LABEL}`;
      const payload = { merchant_id: merchantId, order_reference_number: shipment.order_reference_number };
      const response = await axios.put(apiUrl, payload, {
        headers: { Authorization: authToken, "Content-Type": "application/json" },
      });
      const labelUrl = response.data.shipment_label_presigned_url;
      if (labelUrl) {
        const a = document.createElement("a");
        a.href = labelUrl;
        a.download = `${shipment.order_reference_number}-shipment-label.pdf`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
      } else {
        setLabelError("Label URL not available. Please try again.");
      }
    } catch (error) {
      console.error("Error refetching label:", error);
      setLabelError("Label regeneration failed. Please try again.");
    }
  };

  /* ---------------------- Export PDF (stacked timeline) ---------------------- */
  const handleExportPDF = useCallback(async () => {
    if (!printRootRef.current) return;
    const html2pdf = (await import("html2pdf.js")).default;

    // Show the hidden print layout just for capture
    const node = printRootRef.current;
    const old = node.style.display;
    node.style.display = "block";

    const filename = `${shipment?.order_reference_number || orderRef || "shipment"}-details.pdf`;

    try {
      await html2pdf()
        .set({
          filename,
          margin: [8, 8, 10, 8], // mm
          image: { type: "jpeg", quality: 0.98 },
          html2canvas: { scale: 2, useCORS: true, scrollY: 0 },
          jsPDF: { unit: "mm", format: "a4", orientation: "portrait" },
          pagebreak: { mode: ["css", "legacy"], avoid: ".pdf-avoid-break" },
        })
        .from(node)
        .save();
    } catch (e) {
      console.error("PDF export failed:", e);
    } finally {
      node.style.display = old || "none";
    }
  }, [orderRef, shipment]);

  /* Render gates */
  if (isFetching) {
    return (
      <div className="flex flex-col items-center justify-center py-20">
        <div className="h-10 w-10 animate-spin rounded-full border-2 border-slate-300 border-t-slate-500" />
        <p className="mt-4 text-gray-600 font-medium">Loading shipment details…</p>
      </div>
    );
  }
  if (!shipment) return <div className="p-6 text-gray-700">No shipment found for reference: {orderRef}</div>;

  const sh = shipment;
  const packages = Array.isArray(sh.shipment_packages) ? sh.shipment_packages : [];
  const flatItems = packages.flatMap((pkg) => (pkg.items || []).map((item) => ({ ...item, _pkg: pkg })));
  const itemsTotal = flatItems.reduce((sum, it) => sum + Number(it.quantity ?? 0) * Number(it.item_payment_details?.unit_price ?? 0), 0);

  const latestHistoryISO = getLatestHistoryUpdate(businessStatuses);
  const latestHistoryPretty = latestHistoryISO ? formatInIST(latestHistoryISO) : "N/A";

  const addressLine = (addr, city, dist, state, pin) => [addr, city, dist, state, pin].filter(Boolean).join(", ");

  return (
    <>
      {/* LIVE PAGE (unchanged UI) */}
      <div className="container max-w-screen-2xl mx-auto px-4 lg:px-6 py-12 space-y-8">
        {/* Identity Bar */}
        <IdentityBar
          sh={sh}
          latestBusinessCode={latestBusinessCode}
          currentStatusName={currentStatusName}
          onRefetchLabel={refetchLabel}
          onCancel={handleCancel}
          onExportPDF={handleExportPDF}
          labelError={labelError}
          openDamageClaim={openDamageClaim}
          openWeightDispute={openWeightDispute}
          claimModalOpen={claimModalOpen}
          setClaimModalOpen={setClaimModalOpen}
          shipment={shipment}
          claimModalType={claimModalType}
          onSupportClick={handleOpenSupport}
        />

        {/* KPI row: extras on RIGHT side */}
        <section className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <MetricTile
            label="Invoice Value"
            value={prettyMoney(sh.payment_details?.total_amount)}
            hint={sh.payment_details?.payment_mode}
            extras={[`Items • ${flatItems.length}`, `Packages • ${packages.length}`]}
          />
          <MetricTile
            label="Shipping Cost"
            value={prettyMoney(sh.total_shipping_charge)}
            hint="Total Charges"
            extras={[
              sh.total_shipping_tax ? `Tax • ${prettyMoney(sh.total_shipping_tax)}` : null,
              sh.awb_number ? `AWB • ${sh.awb_number}` : null,
            ]}
          />
          <KpiWeightsCompact
            deadGrams={sh.dead_weight_in_grams}
            volGrams={sh.volumetric_weight_in_grams}
            chargedCategory={(sh.charged_weight_category || "").replace(/_/g, " ")}
          />
          <MetricTile
            label="Order Date"
            value={sh.order_placed_date_time ? formatInIST(sh.order_placed_date_time) : "N/A"}
            inlineValue
            extras={[
              latestHistoryISO ? `Updated • ${latestHistoryPretty}` : null,
              sh.invoice_number ? `Invoice # • ${sh.invoice_number}` : null,
            ]}
          />
        </section>

        {/* Metadata */}
        <MetadataGrid
          title="Shipment Metadata"
          items={[
            { k: "Order Type", v: sh.order_type },
            { k: "Pickup Org", v: sh.pickup_organisation },
            { k: "Service Type", v: sh.service_type },
            { k: "Charged Weight Category", v: (sh.charged_weight_category || "").replace(/_/g, " ") },
          ]}
        />

        {/* Addresses */}
        <ResponsiveAddresses
          pickup={{
            name: sh.pickup_name,
            phone: sh.pickup_poc_contact_number,
            email: sh.pickup_poc_email,
            addr: addressLine(sh.pickup_address, sh.pickup_city, sh.pickup_district, sh.pickup_state, sh.pickup_pincode),
          }}
          delivery={{
            name: sh.drop_name,
            phone: sh.drop_poc_contact_number,
            email: sh.drop_email,
            addr: addressLine(sh.drop_address, sh.drop_city, sh.drop_district, sh.drop_state, sh.drop_pincode),
          }}
          ret={{
            name: sh.return_name || sh.pickup_name,
            phone: sh.return_phone || sh.pickup_poc_contact_number,
            email: sh.return_email || sh.pickup_poc_email,
            addr: addressLine(
              sh.return_address || sh.pickup_address,
              sh.return_city || sh.pickup_city,
              sh.return_district || sh.pickup_district,
              sh.return_state || sh.pickup_state,
              sh.return_pincode || sh.pickup_pincode
            ),
          }}
        />

        {/* Items */}
        <ItemsSection
          packages={packages}
          flatItems={flatItems}
          itemsTotal={itemsTotal}
          shippingCharge={sh.total_shipping_charge}
          labelUrl={sh.order_label_url}
        />

        {/* On-screen horizontal timeline */}
        <TrackingTimeline businessStatuses={businessStatuses} />

        {/* Footer (uses latest history) */}
        <FooterRail
          lastUpdatedISO={latestHistoryISO}
          onBack={() => navigate("/all-shipments", { state: { page: fromPage } })}
          onRefetch={refetchLabel}
          onCancel={handleCancel}
        />
      </div>

      {/* PRINT LAYOUT (hidden) */}
      <div
        ref={printRootRef}
        style={{ display: "none" }}
        className="print-root px-4 py-6"
      >
        <div className="max-w-[900px] mx-auto space-y-10">
          {/* Header */}
          <div className="flex items-start justify-between gap-4 pdf-avoid-break">
            <div>
              <div className="text-xs text-gray-400">Logistics ▸ Shipments ▸ Details</div>
              <h1 className="text-2xl font-bold text-slate-900">Shipment Details</h1>
              <div className="mt-2 grid grid-cols-2 gap-2 text-sm">
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-6 gap-4 mt-2">
                  <div className="lg:col-span-2">
                    <IdField label="Order Reference" value={sh.order_reference_number} mono copyable />
                  </div>
                  <div className="lg:col-span-2">
                    <IdField label="AWB Number" value={sh.awb_number || "—"} mono copyable />
                  </div>
                  <div className="lg:col-span-1">
                    <IdField label="Courier Partner" value={sh.allocated_carrier_partner_public_name || "—"} />
                  </div>
                  <div className="lg:col-span-1">
                    <StatusField 
                      label="Latest Status" 
                      code={latestBusinessCode ?? sh.latest_order_status_code} 
                      value={currentStatusName}
                      shipment={sh}
                      onSupportClick={handleOpenSupport}
                      showSupportForAll={true}
                    />
                  </div>
                </div>
              </div>
            </div>
            <div className="text-xs text-gray-500 text-right">
              <div>Generated on: {format(new Date(), "dd MMM yyyy, hh:mm a")}</div>
              <div>Last updated: {latestHistoryISO ? formatInIST(latestHistoryISO) : "N/A"}</div>
            </div>
          </div>

          {/* KPIs */}
          <div className="grid grid-cols-2 gap-4 pdf-avoid-break">
            <div className="rounded-xl border p-4">
              <div className="text-[11px] uppercase tracking-wide text-slate-500">Invoice Value</div>
              <div className="text-xl font-semibold">{prettyMoney(sh.payment_details?.total_amount)}</div>
              <div className="text-xs text-slate-600 mt-1">{sh.payment_details?.payment_mode || "—"}</div>
              <ul className="text-[11px] text-slate-600 mt-1">
                <li>• Items • {flatItems.length}</li>
                <li>• Packages • {packages.length}</li>
              </ul>
            </div>
            <div className="rounded-xl border p-4">
              <div className="text-[11px] uppercase tracking-wide text-slate-500">Shipping Cost</div>
              <div className="text-xl font-semibold">{prettyMoney(sh.total_shipping_charge)}</div>
              <div className="text-xs text-slate-600 mt-1">Total Charges</div>
              <ul className="text-[11px] text-slate-600 mt-1">
                {sh.total_shipping_tax ? <li>• Tax • {prettyMoney(sh.total_shipping_tax)}</li> : null}
                {sh.awb_number ? <li>• AWB • {sh.awb_number}</li> : null}
              </ul>
            </div>
            <div className="rounded-xl border p-4">
              <div className="text-[11px] uppercase tracking-wide text-slate-500">Weight</div>
              <div className="text-xl font-semibold">
                {(() => {
                  const deadKg = Number.isFinite(Number(sh.dead_weight_in_grams)) ? Number(sh.dead_weight_in_grams) / 1000 : null;
                  const volKg = Number.isFinite(Number(sh.volumetric_weight_in_grams)) ? Number(sh.volumetric_weight_in_grams) / 1000 : null;
                  const chargedKg = deadKg == null && volKg == null ? null : Math.max(deadKg || 0, volKg || 0);
                  return chargedKg == null ? "N/A" : `${chargedKg.toFixed(2)} kg`;
                })()}
              </div>
              <div className="text-xs text-slate-600 mt-1">Dead • {gramsToKg(sh.dead_weight_in_grams)} &nbsp;&nbsp; Vol • {gramsToKg(sh.volumetric_weight_in_grams)}</div>
            </div>
            <div className="rounded-xl border p-4">
              <div className="text-[11px] uppercase tracking-wide text-slate-500">Order Date</div>
              <div className="text-base font-semibold">{sh.order_placed_date_time ? formatInIST(sh.order_placed_date_time) : "N/A"}</div>
              <ul className="text-[11px] text-slate-600 mt-1">
                {latestHistoryISO ? <li>• Updated • {latestHistoryPretty}</li> : null}
                {sh.invoice_number ? <li>• Invoice # • {sh.invoice_number}</li> : null}
              </ul>
            </div>
          </div>

          {/* Metadata */}
          <div className="space-y-3 pdf-avoid-break">
            <h2 className="text-lg font-semibold text-slate-900">Shipment Metadata</h2>
            <div className="grid grid-cols-2 gap-3">
              <div className="rounded-xl border p-3">
                <div className="text-[11px] uppercase tracking-wide text-slate-500">Order Type</div>
                <div className="text-sm font-medium">{sh.order_type || "—"}</div>
              </div>
              <div className="rounded-xl border p-3">
                <div className="text-[11px] uppercase tracking-wide text-slate-500">Pickup Org</div>
                <div className="text-sm font-medium">{sh.pickup_organisation || "—"}</div>
              </div>
              <div className="rounded-xl border p-3">
                <div className="text-[11px] uppercase tracking-wide text-slate-500">Service Type</div>
                <div className="text-sm font-medium">{sh.service_type || "—"}</div>
              </div>
              <div className="rounded-xl border p-3">
                <div className="text-[11px] uppercase tracking-wide text-slate-500">Charged Weight Category</div>
                <div className="text-sm font-medium">{(sh.charged_weight_category || "").replace(/_/g, " ") || "—"}</div>
              </div>
            </div>
          </div>

          {/* Addresses */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {[
              {
                title: "Pickup", rows: [
                  ["Name", sh.pickup_name],
                  ["Phone", sh.pickup_poc_contact_number],
                  ["Email", sh.pickup_poc_email],
                  ["Address", [sh.pickup_address, sh.pickup_city, sh.pickup_district, sh.pickup_state, sh.pickup_pincode].filter(Boolean).join(", ")],
                ]
              },
              {
                title: "Delivery", rows: [
                  ["Customer", sh.drop_name],
                  ["Phone", sh.drop_poc_contact_number],
                  ["Address", [sh.drop_address, sh.drop_city, sh.drop_district, sh.drop_state, sh.drop_pincode].filter(Boolean).join(", ")],
                ]
              },
              {
                title: "Return", rows: [
                  ["Name", sh.return_name || sh.pickup_name],
                  ["Phone", sh.return_phone || sh.pickup_poc_contact_number],
                  ["Address", [
                    sh.return_address || sh.pickup_address,
                    sh.return_city || sh.pickup_city,
                    sh.return_district || sh.pickup_district,
                    sh.return_state || sh.pickup_state,
                    sh.return_pincode || sh.pickup_pincode,
                  ].filter(Boolean).join(", ")],
                ]
              },
            ].map((card, idx) => (
              <div key={idx} className="rounded-xl border p-4">
                <h3 className="text-sm font-semibold text-slate-800">{card.title}</h3>
                <div className="mt-2 space-y-1.5 text-sm">
                  {card.rows.map(([k, v]) => (
                    <div key={k}><span className="text-slate-500">{k}: </span><span className="text-slate-800">{v || "N/A"}</span></div>
                  ))}
                </div>
              </div>
            ))}
          </div>

          {/* Items */}
          <div className="space-y-2">
            <div className="flex items-end justify-between">
              <div>
                <h2 className="text-lg font-semibold text-slate-900">Items</h2>
                <div className="text-sm text-slate-500">{flatItems.length} {flatItems.length === 1 ? "item" : "items"} across {packages.length} {packages.length === 1 ? "package" : "packages"}</div>
              </div>
            </div>

            {packages.map((pkg, idx) => {
              const items = Array.isArray(pkg.items) ? pkg.items : [];
              const subtotal = items.reduce((sum, it) => sum + Number(it.quantity ?? 0) * Number(it.item_payment_details?.unit_price ?? 0), 0);
              return (
                <div key={idx} className="rounded-xl border overflow-hidden pdf-avoid-break">
                  <div className="px-4 py-2 text-sm bg-slate-50 flex items-center justify-between">
                    <div className="font-medium">Package #{idx + 1}</div>
                    <div className="text-slate-600">
                      Dead: {gramsToKg(pkg.package_dead_weight_in_grams)} • Vol: {gramsToKg(pkg.package_volumetric_weight_in_grams)}
                      {pkg.dimensions_in_cm ? ` • ${pkg.dimensions_in_cm.length}×${pkg.dimensions_in_cm.breadth}×${pkg.dimensions_in_cm.height} cm` : ""}
                    </div>
                  </div>
                  <table className="w-full text-sm">
                    <thead className="text-xs uppercase tracking-wide text-slate-600 bg-white">
                      <tr>
                        <th className="px-4 py-2 text-left">SKU</th>
                        <th className="px-4 py-2 text-left">Item Name</th>
                        <th className="px-4 py-2 text-left">Qty</th>
                        <th className="px-4 py-2 text-left">Unit Price</th>
                        <th className="px-4 py-2 text-left">Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {items.length === 0 ? (
                        <tr><td colSpan={5} className="px-4 py-3 text-center text-slate-500">No items in this package.</td></tr>
                      ) : items.map((it, j) => {
                        const sku = it.item_general_details?.sku || "—";
                        const name = it.item_general_details?.item_name || "—";
                        const qty = Number(it.quantity ?? 0);
                        const unit = Number(it.item_payment_details?.unit_price ?? 0);
                        const total = qty * unit;
                        return (
                          <tr key={j} className="border-t">
                            <td className="px-4 py-2 font-mono text-xs">{sku}</td>
                            <td className="px-4 py-2">{name}</td>
                            <td className="px-4 py-2">{qty}</td>
                            <td className="px-4 py-2">{prettyMoney(unit)}</td>
                            <td className="px-4 py-2 font-semibold">{prettyMoney(total)}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                  <div className="px-4 py-2 flex justify-end">
                    <div className="min-w-[240px] rounded-lg border px-3 py-2">
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-slate-600">Subtotal</span>
                        <span className="font-medium">{prettyMoney(subtotal)}</span>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}

            {flatItems.length > 0 && (
              <div className="flex justify-end">
                <div className="min-w-[280px] rounded-xl border px-4 py-3">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-slate-600">Subtotal</span>
                    <span className="font-medium">{prettyMoney(itemsTotal)}</span>
                  </div>
                  <div className="mt-1 flex items-center justify-between text-sm">
                    <span className="text-slate-600">Shipping</span>
                    <span className="font-medium">{prettyMoney(sh.total_shipping_charge)}</span>
                  </div>
                  <div className="mt-2 pt-2 border-t flex items-center justify-between">
                    <span className="text-slate-800 font-semibold">Order Total</span>
                    <span className="text-slate-900 font-bold">
                      {prettyMoney(itemsTotal + Number(sh.total_shipping_charge ?? 0))}
                    </span>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* STACKED TIMELINE (latest on top) */}
          <div className="pdf-avoid-break">
            <h2 className="text-xl font-bold text-slate-900 mb-2">Shipment Tracking History</h2>
            <div className="text-sm text-slate-600 mb-4">
              Order Ref: <span className="font-medium text-slate-800">{sh.order_reference_number}</span>
              {sh.awb_number ? <> &nbsp;•&nbsp; AWB: <span className="font-medium text-slate-800">{sh.awb_number}</span></> : null}
            </div>

            <div className="space-y-3">
              {[...businessStatuses].sort((a, b) => {
                // latest first by created_date_time (fallback order_status_time)
                const aMs = Math.max(safeMs(a.created_date_time), safeMs(a.order_status_time));
                const bMs = Math.max(safeMs(b.created_date_time), safeMs(b.order_status_time));
                return bMs - aMs;
              }).map((st, i) => {
                const whenIso = st.created_date_time || st.order_status_time;
                const when = whenIso ? formatInIST(whenIso) : "N/A";
                const name = getOrderStatusName(String(st.order_status_code));
                return (
                  <div key={i} className="rounded-xl border p-3">
                    <div className="flex items-start justify-between gap-3">
                      <div className="text-sm font-semibold text-slate-900">{name}</div>
                      <div className="text-xs text-slate-600 whitespace-nowrap">{when}</div>
                    </div>
                    <div className="mt-1 text-sm text-slate-700">
                      {st.order_location ? <div>Location: {st.order_location}</div> : null}
                      {st.order_remarks ? <div className="mt-0.5">{st.order_remarks}</div> : null}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>

      {/* Support Ticket Modal */}
      <CreateTicketModal
        isOpen={supportModalOpen}
        onClose={() => {
          setSupportModalOpen(false);
          setSupportOrderContext(null);
        }}
        onSuccess={(ticket) => {
          setSupportModalOpen(false);
          setSupportOrderContext(null);
          navigate(`/support/tickets/${ticket.public_id}`);
        }}
        orderContext={supportOrderContext}
      />

      {/* Utilities & print styles */}
      <style>{`
        .custom-scrollbar::-webkit-scrollbar { height: 8px; }
        .custom-scrollbar::-webkit-scrollbar-track { background: #f1f1f1; }
        .custom-scrollbar::-webkit-scrollbar-thumb { background-color: #c1c1c1; border-radius: 4px; }
        @keyframes fadeIn { from { opacity: 0; transform: translateY(6px);} to { opacity: 1; transform: translateY(0);} }
        .pdf-avoid-break { page-break-inside: avoid; }
        .print-root * { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
      `}</style>
    </>
  );
}

/* ============================== Components ============================== */

function Tooltip({ text, children }) {
  return (
    <span className="group relative inline-block align-middle">
      {children ?? <span className="cursor-help text-slate-400">?</span>}
      <span
        role="tooltip"
        className="pointer-events-none absolute right-0 top-6 z-20 hidden w-64 rounded-md border bg-white p-2 text-xs text-slate-700 shadow-md group-hover:block"
      >
        {text}
      </span>
    </span>
  );
}

function IdField({ label, value, mono, badge, copyable }) {
  const onCopy = () => value && navigator.clipboard?.writeText?.(String(value));
  return (
    <div className="min-w-0">
      <div className="text-[11px] uppercase tracking-wide text-slate-500">{label}</div>
      <div className="mt-0.5 flex items-center gap-2">
        <span title={value || ''} className={`${mono ? 'font-mono' : 'font-medium'} text-slate-900 break-all`}>
          {value || '—'}
        </span>
        {copyable && value && (
          <button onClick={onCopy} className="text-[11px] px-1.5 py-0.5 rounded border hover:bg-slate-50">Copy</button>
        )}
      </div>
    </div>
  );
}

function StatusField({ label, code, value, shipment, onSupportClick, showSupportForAll = false }) {
  const tone = statusPillTone(code);
  const statusCodeStr = String(code ?? "");
  const needsSupport = showSupportForAll || STATUSES_REQUIRING_SUPPORT.includes(statusCodeStr);

  return (
    <div>
      <div className="text-[11px] uppercase tracking-wide text-slate-500">{label}</div>
      <div className="mt-0.5 flex items-center gap-1.5">
        <div className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-semibold ${tone}`}>{value || "—"}</div>
        {needsSupport && shipment && onSupportClick && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              onSupportClick();
            }}
            className="inline-flex items-center justify-center p-1.5 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg transition-all shadow-sm hover:shadow-md"
            title="Create Support Ticket"
          >
            <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
            </svg>
          </button>
        )}
      </div>
    </div>
  );
}

/* -------- Identity bar -------- */
function IdentityBar({
  sh,
  latestBusinessCode,
  currentStatusName,
  onRefetchLabel,
  onCancel,
  labelError,
  onExportPDF,
  openDamageClaim,
  openWeightDispute,
  claimModalOpen,
  setClaimModalOpen,
  shipment,
  claimModalType,
  onSupportClick,
}) {
  return (
    <section className="rounded-2xl border bg-white shadow-sm">
      {/* top ribbon */}
      <div className="h-1 rounded-t-2xl bg-gradient-to-r from-indigo-500 via-sky-500 to-emerald-500" />

      <div className="px-4 md:px-6 py-6">
        <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
          {/* Left: title + identifiers */}
          <div className="min-w-0 flex-1">
            <div className="text-xs text-gray-400">Logistics ▸ Shipments ▸ Details</div>
            <div className="mt-1 flex flex-wrap items-center gap-2">
              <h1 className="text-2xl md:text-3xl font-bold tracking-tight text-slate-900">Shipment Details / {sh.awb_number || "—"}</h1>
              {latestBusinessCode && (
                <div className="flex items-center gap-1.5">
                  <span className={`${statusPillTone(latestBusinessCode)} px-2 py-0.5 rounded-full text-[11px] font-semibold`}>
                    {currentStatusName}
                  </span>
                  {sh && onSupportClick && (
                    <button
                      onClick={onSupportClick}
                      className="inline-flex items-center justify-center p-1.5 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg transition-all shadow-sm hover:shadow-md"
                      title="Create Support Ticket"
                    >
                      <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
                      </svg>
                    </button>
                  )}
                </div>
              )}
            </div>

            {/* Dense information bar (IDs get the width) */}
            <div className="mt-4 grid gap-3 grid-cols-1 md:grid-cols-2 lg:grid-cols-6">
              {/* Order reference: larger on lg */}
              <div className="lg:col-span-2 min-w-0">
                <IdChip label="Order Reference" value={sh.order_reference_number} mono copyable emphasis />
              </div>

              {/* AWB: larger on lg */}
              <div className="lg:col-span-2 min-w-0">
                <IdChip label="AWB Number" value={sh.awb_number || "—"} mono copyable emphasis />
              </div>

              {/* Courier: 1 column on lg */}
              <div className="lg:col-span-1 min-w-0">
                <IdChip label="Courier" value={sh.allocated_carrier_partner_public_name || "—"} />
              </div>

              {/* Latest status: 1 column on lg */}
              <div className="lg:col-span-1 min-w-0">
                <StatusChip
                  label="Latest Status"
                  value={currentStatusName}
                  tone={statusPillTone(latestBusinessCode ?? sh.latest_order_status_code)}
                  statusCode={latestBusinessCode ?? sh.latest_order_status_code}
                  shipment={sh}
                  onSupportClick={onSupportClick}
                  showSupportForAll={true}
                />
              </div>
            </div>

          </div>

          {/* Right: actions */}
          {/* Right: actions */}
          <div className="flex flex-col items-end gap-2 flex-none shrink-0 whitespace-nowrap self-start">
            {/* Primary row */}
            <div className="flex flex-wrap justify-end gap-2">
              <a
                href={sh.awb_number ? `https://tracking.wayuvega.in/track?awb=${encodeURIComponent(sh.awb_number)}` : undefined}
                target="_blank"
                rel="noreferrer"
                className={`px-3 py-2 rounded-lg border border-slate-200 bg-slate-50 text-slate-700 ${sh.awb_number ? "hover:bg-slate-100" : "opacity-60 cursor-not-allowed"
                  }`}
                aria-disabled={!sh.awb_number}
              >
                Live Track
              </a>
              <button
                onClick={onRefetchLabel}
                className="px-3 py-2 rounded-lg bg-indigo-600 text-white hover:bg-indigo-700"
              >
                Refetch Label
              </button>
              <button
                onClick={onExportPDF}
                className="px-3 py-2 rounded-lg bg-slate-800 text-white hover:bg-slate-900"
              >
                Print
              </button>
              <button
                onClick={onCancel}
                className="px-3 py-2 rounded-lg bg-rose-600 text-white hover:bg-rose-700"
              >
                Cancel
              </button>
            </div>

            {/* Secondary row (claim actions) */}
            <div className="flex flex-wrap justify-end gap-2">
              <button
                onClick={openDamageClaim}
                className="px-3 py-2 rounded-lg bg-amber-600 text-white hover:bg-amber-700"
              >
                Raise Damage Claim
              </button>
              <button
                onClick={openWeightDispute}
                className="px-3 py-2 rounded-lg bg-emerald-600 text-white hover:bg-emerald-700"
              >
                Upload Packaging Video
              </button>
            </div>
          </div>

        </div>

        {/* Claim Upload Modal (Damage / Weight Dispute) */}
        <ClaimUploadModal
          open={claimModalOpen}
          onClose={() => setClaimModalOpen(false)}
          shipment={shipment}
          claimType={claimModalType}
        />

        {labelError && <p className="text-rose-600 text-xs mt-2">{labelError}</p>}
      </div>
    </section>
  );
}

/* Chips (compact, enterprise) */
function IdChip({ label, value, mono, copyable, emphasis }) {
  const onCopy = () => value && navigator.clipboard?.writeText?.(String(value));
  return (
    <div className="min-w-0">
      <div className="text-[11px] uppercase tracking-wide text-slate-500">{label}</div>
      <div className={`mt-1 inline-flex max-w-full items-center gap-2 rounded-lg border ${emphasis ? "bg-white" : "bg-slate-50"}
                      px-3 py-2 shadow-sm`}>
        <span title={value || ""} className={`${mono ? "font-mono" : "font-medium"} text-slate-900 text-[15px] leading-5 break-all`}>
          {value || "—"}
        </span>
        {copyable && value && (
          <button onClick={onCopy} className="text-[11px] px-1.5 py-0.5 rounded border hover:bg-slate-50">Copy</button>
        )}
      </div>
    </div>
  );
}
function StatusChip({ label, value, tone, statusCode, shipment, onSupportClick, showSupportForAll = false }) {
  const statusCodeStr = String(statusCode ?? "");
  const needsSupport = showSupportForAll || STATUSES_REQUIRING_SUPPORT.includes(statusCodeStr);

  return (
    <div>
      <div className="text-[11px] uppercase tracking-wide text-slate-500">{label}</div>
      <div className="mt-1 flex items-center gap-1.5">
        <div className={`inline-flex items-center rounded-full px-2.5 py-1 text-xs font-semibold ${tone}`}>{value || "—"}</div>
        {needsSupport && shipment && onSupportClick && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              onSupportClick();
            }}
            className="inline-flex items-center justify-center p-1.5 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg transition-all shadow-sm hover:shadow-md"
            title="Create Support Ticket"
          >
            <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
            </svg>
          </button>
        )}
      </div>
    </div>
  );
}

/* -------- Metric tile (supports inlineValue) -------- */
function MetricTile({ label, value, hint, badge, extras = [], inlineValue = false }) {
  const clean = extras.filter(Boolean);
  return (
    <div className="rounded-xl border bg-white p-4 shadow-sm hover:shadow transition-shadow">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <div className="text-xs uppercase tracking-wide text-slate-500">{label}</div>
            {badge ? <span className="rounded bg-slate-100 text-slate-700 text-[11px] px-2 py-0.5">{badge}</span> : null}
          </div>
          <div className={`${inlineValue ? "text-base font-semibold whitespace-nowrap" : "text-2xl font-semibold"} text-slate-900 leading-tight mt-1`}>
            {value}
          </div>
          {hint ? <div className="mt-1 text-xs text-slate-500">{hint}</div> : null}
        </div>

        {clean.length > 0 && (
          <ul className="text-[11px] text-slate-600 space-y-1 text-right shrink-0">
            {clean.map((line, i) => (
              <li key={i} className="truncate">• {line}</li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

/* -------- Weights tile -------- */
function KpiWeightsCompact({ deadGrams, volGrams, chargedCategory }) {
  const deadKg = Number.isFinite(Number(deadGrams)) ? Number(deadGrams) / 1000 : null;
  const volKg = Number.isFinite(Number(volGrams)) ? Number(volGrams) / 1000 : null;
  const chargedKg = deadKg == null && volKg == null ? null : Math.max(deadKg || 0, volKg || 0);
  const primary = chargedKg == null ? "N/A" : `${chargedKg.toFixed(2)} kg`;

  return (
    <div className="rounded-xl border bg-white p-4 shadow-sm hover:shadow transition-shadow">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="text-xs uppercase tracking-wide text-slate-500">Weight</div>
          <div className="mt-1 text-2xl font-semibold text-slate-900 leading-tight">{primary}</div>
          <div className="mt-2 text-xs text-slate-600 space-x-3">
            <span className="inline-flex items-center gap-1">
              <span className="rounded px-1.5 py-0.5 bg-slate-100 text-slate-700">Dead</span>
              <span>{deadKg == null ? "N/A" : `${deadKg.toFixed(2)} kg`}</span>
            </span>
            <span className="inline-flex items-center gap-1">
              <span className="rounded px-1.5 py-0.5 bg-slate-100 text-slate-700">Vol</span>
              <span>{volKg == null ? "N/A" : `${volKg.toFixed(2)} kg`}</span>
            </span>
          </div>
        </div>
        <div className="text-[11px] text-slate-600 text-right shrink-0">
          {chargedCategory ? <div className="mb-1">• {chargedCategory}</div> : null}
          <Tooltip text="Dead = physical scale weight. Volumetric = dimensions-based. Charged = billable (usually the higher)." />
        </div>
      </div>
    </div>
  );
}

/* -------- Metadata -------- */
function MetadataGrid({ title, items }) {
  const clean = (items || []).filter(Boolean);
  if (clean.length === 0) return null;
  return (
    <section className="space-y-3">
      <h2 className="text-xl font-bold text-slate-900">{title}</h2>
      <dl className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
        {clean.map(({ k, v }, i) => (
          <div key={i} className="rounded-xl border bg-white p-4 shadow-sm">
            <dt className="text-[11px] uppercase tracking-wide text-slate-500">{k}</dt>
            <dd className="mt-1 text-sm font-medium text-slate-900 break-all">{v || "—"}</dd>
          </div>
        ))}
      </dl>
    </section>
  );
}

/* -------- Addresses: responsive (accordion on mobile) -------- */
function ResponsiveAddresses({ pickup, delivery, ret }) {
  const [open, setOpen] = useState({ p: false, d: false, r: false });
  const Card = ({ title, children, keyName }) => (
    <div className="rounded-xl border bg-white p-5 shadow-sm">
      <button
        className="w-full flex items-center justify-between md:cursor-default"
        onClick={() => setOpen((o) => ({ ...o, [keyName]: !o[keyName] }))}
      >
        <h3 className="text-sm font-semibold text-slate-800">{title}</h3>
        <span className="md:hidden text-slate-500 text-xs">{open[keyName] ? "Hide" : "Show"}</span>
      </button>
      <div className={`mt-3 space-y-1.5 ${open[keyName] ? "" : "hidden md:block"}`}>{children}</div>
    </div>
  );

  const Row = ({ label, value }) => (
    <div className="text-sm">
      <span className="text-slate-500">{label}: </span>
      <span className="text-slate-800 break-words">{value || "N/A"}</span>
    </div>
  );

  return (
    <section className="grid grid-cols-1 md:grid-cols-3 gap-6">
      <Card title="Pickup" keyName="p">
        <Row label="Name" value={pickup.name} />
        <Row label="Phone" value={pickup.phone} />
        {pickup.email ? <Row label="Email" value={pickup.email} /> : null}
        <Row label="Address" value={pickup.addr} />
      </Card>
      <Card title="Delivery" keyName="d">
        <Row label="Customer" value={delivery.name} />
        <Row label="Phone" value={delivery.phone} />
        {delivery.email ? <Row label="Email" value={delivery.email} /> : null}
        <Row label="Address" value={delivery.addr} />
      </Card>
      <Card title="Return" keyName="r">
        <Row label="Name" value={ret.name} />
        <Row label="Phone" value={ret.phone} />
        {ret.email ? <Row label="Email" value={ret.email} /> : null}
        <Row label="Address" value={ret.addr} />
      </Card>
    </section>
  );
}

/* -------- Section header / empty state -------- */
function SectionHeader({ title, subtitle, right }) {
  return (
    <div className="mb-3 flex flex-wrap items-end justify-between gap-3">
      <div>
        <h2 className="text-xl font-bold text-slate-900">{title}</h2>
        {subtitle && <p className="text-sm text-slate-500">{subtitle}</p>}
      </div>
      {right}
    </div>
  );
}
function EmptyState({ label }) {
  return <div className="rounded-xl border border-dashed p-6 text-center text-sm text-slate-500 bg-slate-50">{label}</div>;
}

/* -------- Items -------- */
function ItemsSection({ packages, flatItems, itemsTotal, shippingCharge, labelUrl }) {
  return (
    <section className="space-y-3">
      <SectionHeader
        title="Items"
        subtitle={`${flatItems.length} ${flatItems.length === 1 ? "item" : "items"} across ${packages.length} ${packages.length === 1 ? "package" : "packages"}`}
        right={
          labelUrl ? (
            <a href={labelUrl} target="_blank" rel="noreferrer" className="text-indigo-700 hover:text-indigo-900 text-sm underline">
              View Label
            </a>
          ) : null
        }
      />
      {packages.length === 0 ? (
        <EmptyState label="No package/items found for this shipment." />
      ) : (
        packages.map((pkg, idx) => <PackageTable key={idx} pkg={pkg} />)
      )}

      {flatItems.length > 0 && (
        <div className="flex justify-end">
          <div className="min-w-[280px] rounded-xl border bg-white px-4 py-3 shadow-sm">
            <div className="flex items-center justify-between text-sm">
              <span className="text-slate-600">Subtotal</span>
              <span className="font-medium">{prettyMoney(itemsTotal)}</span>
            </div>
            <div className="mt-1 flex items-center justify-between text-sm">
              <span className="text-slate-600">Shipping</span>
              <span className="font-medium">{prettyMoney(shippingCharge)}</span>
            </div>
            <div className="mt-2 pt-2 border-t flex items-center justify-between">
              <span className="text-slate-800 font-semibold">Order Total</span>
              <span className="text-slate-900 font-bold">
                {prettyMoney(itemsTotal + Number(shippingCharge ?? 0))}
              </span>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}

/* -------- Single package table -------- */
function PackageTable({ pkg }) {
  const [open, setOpen] = useState(true);
  const items = Array.isArray(pkg.items) ? pkg.items : [];
  const subtotal = items.reduce((sum, it) => sum + Number(it.quantity ?? 0) * Number(it.item_payment_details?.unit_price ?? 0), 0);

  return (
    <div className="rounded-xl border bg-white shadow-sm overflow-hidden">
      <button
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center justify-between px-4 py-3 text-left hover:bg-slate-50"
        aria-expanded={open}
      >
        <div className="flex items-center gap-3">
          <span className={`inline-flex h-6 w-6 items-center justify-center rounded-md border text-xs ${open ? "bg-slate-800 text-white border-slate-800" : "text-slate-700"}`}>
            {open ? "–" : "+"}
          </span>
          <div>
            <div className="text-sm font-semibold text-slate-800">Package</div>
            <div className="text-xs text-slate-500">
              Dead: {gramsToKg(pkg.package_dead_weight_in_grams)} • Vol: {gramsToKg(pkg.package_volumetric_weight_in_grams)}
              {pkg.dimensions_in_cm ? ` • ${pkg.dimensions_in_cm.length}×${pkg.dimensions_in_cm.breadth}×${pkg.dimensions_in_cm.height} cm` : ""}
            </div>
          </div>
        </div>
        <div className="text-sm text-slate-600">
          Items: <span className="font-medium">{items.length}</span> • Subtotal: <span className="font-semibold">{prettyMoney(subtotal)}</span>
        </div>
      </button>

      {open && (
        <div className="overflow-x-auto">
          <table className="w-full table-auto border-t">
            <thead className="bg-slate-50 text-slate-600 text-xs uppercase tracking-wide">
              <tr>
                <th className="px-4 py-2 text-left">SKU</th>
                <th className="px-4 py-2 text-left">Item Name</th>
                <th className="px-4 py-2 text-left">Qty</th>
                <th className="px-4 py-2 text-left">Unit Price</th>
                <th className="px-4 py-2 text-left">Total</th>
              </tr>
            </thead>
            <tbody className="text-sm">
              {items.map((it, j) => {
                const sku = it.item_general_details?.sku || "—";
                const name = it.item_general_details?.item_name || "—";
                const qty = Number(it.quantity ?? 0);
                const unit = Number(it.item_payment_details?.unit_price ?? 0);
                const total = qty * unit;
                return (
                  <tr key={j} className="border-t last:border-b-0 hover:bg-slate-50/60">
                    <td className="px-4 py-2 font-mono text-xs">{sku}</td>
                    <td className="px-4 py-2">{name}</td>
                    <td className="px-4 py-2">{qty}</td>
                    <td className="px-4 py-2">{prettyMoney(unit)}</td>
                    <td className="px-4 py-2 font-semibold">{prettyMoney(total)}</td>
                  </tr>
                );
              })}
              {items.length === 0 && (
                <tr>
                  <td colSpan={5} className="px-4 py-4 text-center text-slate-500">
                    No items in this package.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
/* -------- Tracking history  -------- */
function TrackingTimeline({ businessStatuses = [] }) {
  const [filter, setFilter] = React.useState("ALL");
  if (!businessStatuses.length) {
    return (
      <section className="space-y-3">
        <h2 className="text-xl font-bold text-slate-900">Shipment Tracking History</h2>
        <EmptyState label="No tracking statuses found." />
      </section>
    );
  }

  // normalize + sort latest → oldest
  const rows = [...businessStatuses]
    .map(s => ({
      ...s,
      whenMs: Math.max(safeMs(s.created_date_time), safeMs(s.order_status_time)),
      whenISO: s.created_date_time || s.order_status_time,
    }))
    .sort((a, b) => b.whenMs - a.whenMs);

  const passes = (s) => {
    if (filter === "ALL") return true;
    const code = String(s.order_status_code || "");
    if (filter === "EXC") return ["110", "120", "170"].includes(code);          // cancelled/failed/rtx
    if (filter === "LOC") return Boolean(s.order_location);
    if (filter === "ATT") return /attempt/i.test(s.order_remarks || "");
    return true;
  };

  const filtered = rows.filter(passes);

  // group by calendar day
  const groups = filtered.reduce((acc, s) => {
    const d = s.whenISO ? format(parseISO(s.whenISO), "EEE, dd MMM yyyy") : "Unknown";
    (acc[d] ||= []).push(s);
    return acc;
  }, {});

  return (
    <section className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-bold text-slate-900">Shipment Tracking History</h2>

        {/* filters */}
        <div className="flex gap-1.5">
          {[
            ["ALL", "All"],
            ["EXC", "Exceptions"],
            ["LOC", "Location"],
            ["ATT", "Attempts"],
          ].map(([key, label]) => (
            <button
              key={key}
              onClick={() => setFilter(key)}
              className={`px-2.5 py-1 rounded-full text-xs border
                ${filter === key ? "bg-slate-900 text-white border-slate-900" : "bg-white hover:bg-slate-50"}`}
            >
              {label}
            </button>
          ))}
          <CopyHistoryButton items={filtered} />
        </div>
      </div>

      {/* rail */}
      <div className="relative">
        <div className="absolute left-4 top-0 bottom-0 w-[3px] rounded bg-slate-200" />
        <div className="space-y-6">
          {Object.entries(groups).map(([day, list]) => (
            <div key={day} className="relative pl-12">
              {/* day header */}
              <div className="mb-2 inline-flex items-center gap-2 rounded-full border bg-white px-2.5 py-1 text-xs text-slate-700 shadow-sm">
                <span className="font-medium">{day}</span>
                <span className="h-1 w-1 rounded-full bg-slate-300" />
                <span>{list.length} update{list.length > 1 ? "s" : ""}</span>
              </div>

              {/* items */}
              <ul className="space-y-3">
                {list.map((st, idx) => {
                  const latest = idx === 0 && list === Object.values(groups)[0];
                  const name = getOrderStatusName(String(st.order_status_code));
                  const tone = statusPillTone(st.order_status_code);
                  const when = st.whenISO ? formatInIST(st.whenISO) : "N/A";
                  const isException = ["110", "120", "170"].includes(String(st.order_status_code || ""));
                  return (
                    <li key={st.whenISO + idx} className="relative">
                      {/* marker */}
                      <span className={`absolute left-2 top-4 -translate-x-1/2 inline-flex items-center justify-center
                                       h-8 w-8 rounded-full ring-2 ring-white ${tone}`}>
                        <span className="text-white/90">
                          <svg viewBox="0 0 20 20" className="h-4 w-4" fill="currentColor"><path d="M10 2a8 8 0 1 0 .001 16.001A8 8 0 0 0 10 2zm1 11H9V9h2v4zm0-6H9V5h2v2z" /></svg>
                        </span>
                        {latest && <span className="absolute inline-flex h-8 w-8 rounded-full animate-ping opacity-25 bg-sky-400" />}
                      </span>

                      {/* card */}
                      <div className={`group rounded-xl border bg-white shadow-sm hover:shadow-md transition-shadow pl-4`}>
                        <div className="p-3 sm:p-4">
                          <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
                            <span className="text-sm font-semibold text-slate-900">{name}</span>
                            {isException && (
                              <span className="inline-flex items-center rounded-full bg-rose-50 text-rose-700 ring-1 ring-rose-200 px-2 py-0.5 text-[11px] font-semibold">
                                Exception
                              </span>
                            )}
                            <span className="ml-auto text-xs text-slate-500">{when}</span>
                          </div>

                          <div className="mt-2 grid gap-2 sm:grid-cols-2">
                            {st.order_location && (
                              <div className="text-sm text-slate-700">
                                <span className="text-slate-500">Location:&nbsp;</span>
                                <span className="font-medium">{st.order_location}</span>
                              </div>
                            )}
                            {st.order_status_code && (
                              <div className="text-sm text-slate-700">
                                <span className="text-slate-500">Code:&nbsp;</span>
                                <span className="font-mono">{String(st.order_status_code)}</span>
                              </div>
                            )}
                          </div>

                          {st.order_remarks && (
                            <div className="mt-2 rounded-lg bg-slate-50 border text-sm text-slate-700 p-3">
                              {st.order_remarks}
                            </div>
                          )}
                        </div>

                        {/* footer accent */}
                        <div className={`h-1 rounded-b-xl ${isException ? "bg-rose-200" : "bg-gradient-to-r from-indigo-200 via-sky-200 to-emerald-200"} opacity-80`} />
                      </div>
                    </li>
                  );
                })}
              </ul>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function CopyHistoryButton({ items }) {
  const copy = () => {
    const lines = items.map(s => {
      const t = s.whenISO ? formatInIST(s.whenISO) : "N/A";
      return `${t} | ${getOrderStatusName(String(s.order_status_code))} | ${s.order_location || "-"} | ${s.order_remarks || "-"}`;
    });
    navigator.clipboard?.writeText(lines.join("\n"));
  };
  return (
    <button onClick={copy} className="ml-2 px-2.5 py-1 rounded border text-xs hover:bg-slate-50">Copy history</button>
  );
}


/* -------- Footer rail (driven by history latest) -------- */
function FooterRail({ lastUpdatedISO, onBack, onRefetch, onCancel }) {
  return (
    <div className="flex flex-wrap items-center justify-between gap-3 border-t pt-4">
      <div className="text-xs text-gray-500">
        Last updated: <span className="font-medium">{lastUpdatedISO ? formatInIST(lastUpdatedISO) : "N/A"}</span>
      </div>
      <div className="flex gap-2">
        <button onClick={onBack} className="px-3 py-2 rounded-lg border text-slate-700 hover:bg-slate-50">← Back to Shipments</button>
        <button onClick={onRefetch} className="px-3 py-2 rounded-lg bg-indigo-600 text-white hover:bg-indigo-700">Refetch Label</button>
        <button onClick={onCancel} className="px-3 py-2 rounded-lg bg-rose-600 text-white hover:bg-rose-700">Cancel Shipment</button>
      </div>
    </div>
  );
}
